import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, Alert, useMediaQuery, Switch, FormControlLabel } from '@mui/material';
import CommonLayout from '../../_layouts/CommonLayout';
import { translate as t } from 'hooks/useTranslations';
import { ManageSolutionsProvider, useManageSolutionsContext } from './context';
import { SolutionWithActive } from './_types';
import Detail from './Detail';
import ManageSolutionsList from './List';
import { RISK_POOL_AVAILABLE_SOLUTIONS } from './Detail/RiskPoolSolutions';

const ManageSolutionsContent: React.FC = () => {

  const [currentPage, setCurrentPage] = useState(0);
  const { selectedSolution, setSelectedSolution } = useManageSolutionsContext();

  const {
    orgSolutions: organizationSolutions,
    orgSolutionsError,
    isLoadingAllSolutions,
    isLoadingOrgSolutions,
    allSolutions,
    uiIsLoading,
    setUiIsLoading,
    setUiIsSaving,
    orgRiskPoolEnabled,
    toggleOrgRiskPool,
    isLoadingOrgRiskPool,
    isMutatingOrgRiskPool,
  } = useManageSolutionsContext();

  const error = orgSolutionsError;

  // Build a local array mirroring allSolutions and flag which are active in the organization
  const solutionsWithActive: SolutionWithActive[] = React.useMemo(() => {
    const orgIds = new Set((organizationSolutions ?? []).map((s) => s.id));
    const allSolutionsWithActive = (allSolutions ?? []).map((s) => ({
      ...s,
      active: orgIds.has(s.id),
    }));

    // If RiskPool is enabled, filter to show only Risk Pool available solutions
    if (orgRiskPoolEnabled) {
      return allSolutionsWithActive.filter((s) => RISK_POOL_AVAILABLE_SOLUTIONS.includes(s.key));
    }

    // For non-risk pool organizations, filter out Performance Reporting
    // (Performance Reporting is exclusively available for Risk Pool organizations)
    return allSolutionsWithActive.filter((s) => s.key !== 'performance-reporting');
  }, [allSolutions, organizationSolutions, orgRiskPoolEnabled]);

  useEffect(() => {
    if (!isLoadingAllSolutions && !isLoadingOrgSolutions) {
      setUiIsLoading(false);
    }
  }, [isLoadingAllSolutions, isLoadingOrgSolutions, setUiIsLoading]);

  // Pagination logic - use solutionsWithActive instead of organizationSolutions
  const isSmallScreen = useMediaQuery('(max-width:1027px)');
  const isLargeScreen = useMediaQuery('(min-width:1601px)');
  const solutionsPerPage = isSmallScreen ? 3 : isLargeScreen ? 5 : 4;
  const totalPages = solutionsWithActive ? Math.ceil(solutionsWithActive.length / solutionsPerPage) : 0;
  const startIndex = currentPage * solutionsPerPage;
  const endIndex = startIndex + solutionsPerPage;
  const currentSolutions = solutionsWithActive ? solutionsWithActive.slice(startIndex, endIndex) : [];

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleSolutionSelect = (solution: SolutionWithActive) => {
    setSelectedSolution(selectedSolution?.id === solution.id ? null : solution);
  };

  const handleToggleOrgRiskPoolChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      // Clear selected solution when toggling Risk Pool mode
      setSelectedSolution(null);
      await toggleOrgRiskPool(event.target.checked);
    } catch (error) {
      console.error('Failed to toggle risk pool:', error);
    }
  };

  // Check if any solution is enabled (regardless of RiskPool or regular)
  // Toggle should be disabled when ANY solution is active
  const hasSolutionsEnabled = React.useMemo(() => {
    return (organizationSolutions ?? []).length > 0;
  }, [organizationSolutions]);

  // Determine if toggle should be disabled
  // Disabled when: any solution is enabled OR loading/mutating
  const isToggleDisabled = hasSolutionsEnabled || isLoadingOrgRiskPool || isMutatingOrgRiskPool;
  console.log('isToggleDisabled', isToggleDisabled, hasSolutionsEnabled, isLoadingOrgRiskPool, isMutatingOrgRiskPool);
  if (uiIsLoading || isLoadingAllSolutions || isLoadingOrgSolutions || isLoadingOrgRiskPool) {
    return (
      <CommonLayout
        headerProps={{
          title: t('manageSolutions.title'),
          subtitle: t('manageSolutions.description'),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      </CommonLayout>
    );
  }

  if (error) {
    return (
      <CommonLayout
        headerProps={{
          title: t('manageSolutions.title'),
          subtitle: t('manageSolutions.description'),
        }}
      >
        <Alert severity='error'>Error loading solutions: {error.message || 'Unknown error'}</Alert>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout
      headerProps={{
        title: t('manageSolutions.title'),
        subtitle: t('manageSolutions.description'),
        actions:
          !isLoadingAllSolutions && !isLoadingOrgSolutions ? (
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={orgRiskPoolEnabled}
                    onChange={handleToggleOrgRiskPoolChange}
                    color='success'
                    disabled={isToggleDisabled}
                  />
                }
                label='Risk Pool Organization'
                labelPlacement='start'
                sx={{ gap: 3 }}
              />
              {isMutatingOrgRiskPool && <CircularProgress size={16} />}
            </Stack>
          ) : undefined,
      }}
    >
      <Stack
        direction='column'
        spacing={4}
        sx={{ height: '100%' }}
      >
        {/* Top Section - Solution Selection */}
        <Box sx={{ width: '100%' }}>
          {/* Solution Cards - Grid Layout */}
          <ManageSolutionsList
            currentSolutions={currentSolutions}
            selectedSolution={selectedSolution}
            onSolutionSelect={handleSolutionSelect}
            currentPage={currentPage}
            totalPages={totalPages}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
          />
        </Box>

        {/* Bottom Section - Solution Details */}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Detail />
        </Box>
      </Stack>
    </CommonLayout>
  );
};

export default function ManageSolutions() {
  return (
    <ManageSolutionsProvider>
      <ManageSolutionsContent />
    </ManageSolutionsProvider>
  );
}
